// BL-2466 (PRJ-93 M1) — the one format for an @mention inside an internal note.
// A mention is stored as `@[staff:<uuid>]` in the plain-text body. These pure helpers
// (ADR 010 — Isomorphic Services) are shared by the note editor, the read-only note
// renderer, the server's mention check and, later, the notification renderer.

import type { InternalNoteEntityType } from '../../schemas/responses/internal-note';
// BL-2481 — kept out of the registry file so the API-surface generators' import
// closure doesn't grow by the role-grant graph.
import { NOTE_READ } from '../../api/registry/internal_notes';
import { rolesHoldGrantDeclaration } from '../auth/role-grants';
import type { Role } from '../auth/roles';

export { MENTION_CHIP_CLASS, OWN_MENTION_CHIP_CLASS, mentionChipClass } from './mention-chip';
export { foldForMatch, mentionNameMatches } from './mention-match';

/** One run of a note body: plain text, or a mention of one staff member. */
export type NoteSegment = { kind: 'text'; text: string } | { kind: 'mention'; staffUserId: string };

/**
 * The stored token. Exact on purpose: lower-case UUID only, so an email address,
 * a lone "@" or a hand-typed look-alike is never read as a mention (RAC 2).
 */
const MENTION_TOKEN = /@\[staff:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]/g;

/**
 * BUG-2257 — text that merely looks like a token ("@[staff:", with any backslashes
 * between the "@" and the "[") is stored with one more backslash, so only a chip can
 * write a real token. {@link parseNoteBody} takes the backslash back off.
 */
const ESCAPABLE_TOKEN_OPENER = /@(\\*)\[staff:/g;
const ESCAPED_TOKEN_OPENER = /@(\\+)\[staff:/g;

/** Store text that a person typed or pasted, so it can never read back as a mention. */
function escapeLiteralText(text: string): string {
  return text.replace(ESCAPABLE_TOKEN_OPENER, (_m, slashes: string) => `@${slashes}\\[staff:`);
}

/** The inverse of {@link escapeLiteralText}: the text exactly as it was typed. */
function unescapeLiteralText(text: string): string {
  return text.replace(ESCAPED_TOKEN_OPENER, (_m, slashes: string) => `@${slashes.slice(1)}[staff:`);
}

/**
 * Write the token for one staff member.
 *
 * @param staffUserId - The mentioned `staff_users.id`.
 * @returns `@[staff:<uuid>]`.
 */
export function formatMentionToken(staffUserId: string): string {
  return `@[staff:${staffUserId}]`;
}

/**
 * Split a stored body into text and mention runs, in order. Empty text runs are
 * dropped, so a body that is only a token is one mention segment.
 *
 * @param body - The stored note text.
 * @returns The body's segments.
 */
export function parseNoteBody(body: string): NoteSegment[] {
  const segments: NoteSegment[] = [];
  let cursor = 0;
  for (const match of body.matchAll(MENTION_TOKEN)) {
    const start = match.index ?? 0;
    if (start > cursor) segments.push({ kind: 'text', text: unescapeLiteralText(body.slice(cursor, start)) });
    segments.push({ kind: 'mention', staffUserId: match[1] });
    cursor = start + match[0].length;
  }
  if (cursor < body.length) segments.push({ kind: 'text', text: unescapeLiteralText(body.slice(cursor)) });
  return segments;
}

/**
 * The staff members a body mentions, each once, in first-seen order (AC 6).
 *
 * @param body - The stored note text.
 * @returns Unique mentioned staff user ids.
 */
export function mentionedStaffIds(body: string): string[] {
  const ids = new Set<string>();
  for (const segment of parseNoteBody(body)) {
    if (segment.kind === 'mention') ids.add(segment.staffUserId);
  }
  return [...ids];
}

/** An inline node of the notes editor's document (plain JSON, no editor import). */
export type NoteEditorInline =
  | { type: 'text'; text: string }
  | { type: 'hardBreak' }
  | { type: 'mention'; attrs: { id: string; label: string } };

/** A paragraph of the notes editor's document. */
export interface NoteEditorParagraph {
  type: 'paragraph';
  content?: NoteEditorInline[];
}

/** The notes editor's document, as Tiptap's `getJSON()` returns it for its schema. */
export interface NoteEditorDoc {
  type: 'doc';
  content?: NoteEditorParagraph[];
}

/** One inline node as stored text: a token, a newline, or the text itself. */
function inlineToText(node: NoteEditorInline): string {
  if (node.type === 'mention') return formatMentionToken(node.attrs.id);
  if (node.type === 'hardBreak') return '\n';
  return escapeLiteralText(node.text);
}

/**
 * Serialize the editor's document to the stored body. A chip becomes its id token,
 * a hard break and a paragraph boundary (pasted text) each become a newline.
 *
 * @param doc - The editor document.
 * @returns The body to store.
 */
export function editorDocToNoteBody(doc: NoteEditorDoc): string {
  return (doc.content ?? [])
    .map((paragraph) => (paragraph.content ?? []).map(inlineToText).join(''))
    .join('\n');
}

/**
 * Rebuild the editor's document from a stored body: one paragraph, newlines as hard
 * breaks, each token as a chip labelled with the staff member's current name.
 *
 * @param body - The stored note text.
 * @param labelFor - The current display name for a mentioned staff user id.
 * @returns The editor document. {@link editorDocToNoteBody} returns `body` unchanged.
 */
export function noteBodyToEditorDoc(body: string, labelFor: (staffUserId: string) => string): NoteEditorDoc {
  const content: NoteEditorInline[] = [];
  for (const segment of parseNoteBody(body)) {
    if (segment.kind === 'mention') {
      content.push({ type: 'mention', attrs: { id: segment.staffUserId, label: labelFor(segment.staffUserId) } });
      continue;
    }
    segment.text.split('\n').forEach((line, index) => {
      if (index > 0) content.push({ type: 'hardBreak' });
      if (line) content.push({ type: 'text', text: line });
    });
  }
  return { type: 'doc', content: [content.length > 0 ? { type: 'paragraph', content } : { type: 'paragraph' }] };
}

/** The inline refusal for a note that mentions someone who may not be mentioned (AC 8). */
export const MENTION_NOT_ALLOWED_MESSAGE = 'You can only mention teammates who can open this record.';

/**
 * BUG-2267 — the refusal a writer reads when a note mentions someone who can't be
 * mentioned, naming them so the writer knows which chip to remove. Only names the
 * server was free to give (a teammate of this house) belong in `names`; when it has
 * none, the fixed {@link MENTION_NOT_ALLOWED_MESSAGE} says nothing about anyone.
 *
 * @param names - Display names of the refused teammates, in the order the note names them.
 * @returns e.g. "Jordan Lee can't be mentioned here. They can't open this record."
 */
export function mentionRefusalMessage(names: readonly string[]): string {
  const unique = [...new Set(names)];
  if (unique.length === 0) return MENTION_NOT_ALLOWED_MESSAGE;
  const list = unique.length === 1 ? unique[0] : `${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`;
  return `${list} can't be mentioned here. They can't open this record.`;
}

/** Why a teammate can't be mentioned on a note of this kind (D5): they can't open it. */
export const MENTION_UNAVAILABLE_REASON: Record<InternalNoteEntityType, string> = {
  lot: "Can't open lots",
  auction: "Can't open auctions",
  customer: "Can't open customers",
  invoice: "Can't open invoices",
  settlement: "Can't open settlements",
};

/**
 * Can this member of the note's house open the note's record? The one rule behind
 * the @ list, the save's mention check and the drain's send-time re-check (BL-2481),
 * so the three can't drift. It mirrors the route gate (ADR 062 — Access Grants):
 * a super admin holds every grant at every house (BUG-2273), but only as a live
 * member, so a person with no role in the house (`roles` empty: they left, were
 * deactivated or never joined) can open nothing. Fails closed on an entity type
 * with no grant.
 *
 * @param member.roles - The person's live roles in the note's house (empty when not a live member).
 * @param member.isSuperAdmin - Whether the person is a platform super admin.
 * @param entityType - The kind of record the note sits on.
 * @returns `true` when a live member holds that record kind's {@link NOTE_READ} grant, or is a super admin.
 */
export function memberCanOpenNote(
  member: { roles: readonly Role[]; isSuperAdmin: boolean },
  entityType: string,
): boolean {
  const grant = NOTE_READ[entityType as keyof typeof NOTE_READ];
  if (grant == null || member.roles.length === 0) return false;
  return member.isSuperAdmin || rolesHoldGrantDeclaration(member.roles, grant);
}
